/*!
  Pioneer WFRIA Form v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Form"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Form") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Form");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children", "onSubmit", "onValidate", "className", "spacing"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { mergeRefs } from "../common/utilities";
import Box from "../private/Box/Box";
import { useForm } from "../private/ThirdParty";
import { FormContext } from "./FormContext";
/**
 * Form is a React component which renders HTML form elements
 * while also providing the validity status of the form.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const Form = /*#__PURE__*/React.forwardRef((_ref, userRef) => {
  let {
    children,
    onSubmit,
    onValidate,
    className,
    spacing
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const formRef = useRef(); // Used to collect default data, later applied on `reset` (react-hook-form suggests this).

  const initialFormValuesRef = useRef();
  const previousFormValidityRef = useRef();
  const validateTimestampRef = useRef();
  const {
    formState,
    handleSubmit: reactHookFormHandleSubmit,
    getValues,
    register,
    reset,
    resetField,
    setValue,
    trigger,
    unregister,
    watch
  } = useForm({
    mode: "all",
    shouldFocusError: true,
    shouldUseNativeValidation: false
  });
  const [pendingReset, setPendingReset] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const {
    errors,
    isValid,
    isDirty,
    isSubmitting,
    submitCount
  } = formState;

  const internalReset = () => {
    setResetDone(false);
    setPendingReset(true);
  };

  const handleValidate = () => {
    /**
     * Adding unique timestamp to the FormContext.
     * This will be used by TextInput and Fieldset to
     * trigger a validation on their end, even if the
     * fields they are in charge of have not been
     * dirtied before (blurred).
     */
    validateTimestampRef.current = new Date().getTime();
    trigger();
  };

  const handleSubmit = e => {
    const internalSubmit = data => {
      onSubmit(e, data, formRef);
    };

    e.preventDefault();
    handleValidate();
    reactHookFormHandleSubmit(internalSubmit)();
  };

  const validateField = args => {
    /**
     * Adding unique timestamp to the FormContext.
     * This will be used by TextInput and Fieldset to
     * trigger a validation on their end, even if the
     * fields they are in charge of have not been
     * dirtied before (blurred).
     */
    validateTimestampRef.current = new Date().getTime();
    trigger(args);
  };

  useEffect(() => {
    if (pendingReset) {
      reset(initialFormValuesRef.current);
      setResetDone(true);
      setPendingReset(false);
    }
  }, [pendingReset, reset]);
  useEffect(() => {
    if (previousFormValidityRef.current !== isValid) {
      previousFormValidityRef.current = isValid;
      onValidate(isValid);
    }
  }, [formState, onValidate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Collect initial form values. These values are later applied when resetting the form.
    if (!initialFormValuesRef.current) {
      initialFormValuesRef.current = getValues();
    } // The user has passed a `ref`, expose WFRIA Form methods.


    if (userRef) {
      // Methods exposed in the API documentation.
      userRef.current.reset = internalReset;
      userRef.current.resetField = resetField;
      userRef.current.validate = handleValidate;
      userRef.current.validateField = validateField; // Methods not exposed in the API documentation.

      userRef.current.getValues = getValues;
      userRef.current.formState = {
        isValid,
        isDirty
      };
    }
  });
  const context = {
    errors,
    getValues,
    isSubmitting,
    register,
    reset,
    resetDone,
    setValue,
    trigger,
    unregister,
    validateField,
    validateTimestamp: validateTimestampRef.current,
    watch,
    submitCount
  };
  return _jsx(Box, {
    spacing: spacing,
    className: className,
    display: "block",
    children: _jsx(FormContext.Provider, {
      value: context,
      children: _jsx("form", _objectSpread(_objectSpread({
        ref: mergeRefs([formRef, userRef]),
        onSubmit: handleSubmit,
        onReset: internalReset,
        noValidate: true
      }, otherProps), {}, {
        children: children
      }))
    })
  });
});
Form.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Form Field components to be displayed inside the Form container. */
  children: PropTypes.node.isRequired,

  /**
   * Callback handler to be executed when the form is submitted.
   * Arguments received are:
   *  - `event`: a synthetic event (can be used to preventDefault).
   *  - `data`: an object containing all the fields being submitted.
   *  - `formRef`: a reference to the <form> DOM element instance.
   */
  onSubmit: PropTypes.func.isRequired,

  /** This is what will be displayed inside the component. */
  className: PropTypes.string,

  /**
   * Callback handler to be executed on validity change of the Form.
   * Useful to enable or disable a form submit button.
   *
   * NOTE: This is only called when the validity _changes_.
   * It __will not__ be invoked each time validation
   * runs on the fields in the Form.
   *
   * Argument received is:
   * - `validity`: validity state of the form
   */
  onValidate: PropTypes.func,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()])
} : {};
Form.defaultProps = {
  className: "",
  onValidate: () => {},
  spacing: {}
};
Form.displayName = "Form";
export default Form;